<script setup>
// Phase 2.9.8 H — aba VOIP no detalhe do KanbanItem.
// Mostra historico VOIP do contato vinculado: chamadas recentes,
// agendamentos, score timeline e ultima disposition.
//
// So e adicionada em KanbanTabs.vue quando item.voip_summary esta presente
// (o que ja e gated pelo backend por account.feature_enabled('voip_calls')).
import { ref, computed, onMounted, watch } from 'vue';
import VoipLeadsAPI from 'dashboard/api/voipLeads';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const props = defineProps({
  item: { type: Object, required: true },
});

const loading = ref(true);
const data = ref(null);
const error = ref(null);

const contactId = computed(() => props.item?.conversation?.contact?.id);

async function load() {
  if (!contactId.value) {
    loading.value = false;
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const { data: res } = await VoipLeadsAPI.show(contactId.value);
    data.value = res;
  } catch (e) {
    error.value = e?.response?.data?.error || 'Erro ao carregar dados VOIP';
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(contactId, load);

// === Helpers de formatacao ===
function formatDate(iso) {
  if (!iso) return '';
  try {
    return format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return iso;
  }
}
function relativeTime(iso) {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), {
      addSuffix: true,
      locale: ptBR,
    });
  } catch {
    return '';
  }
}
function formatDuration(seconds) {
  if (!seconds || seconds < 1) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m${s}s` : `${s}s`;
}
function formatMoney(cents) {
  if (!cents) return 'R$ 0,00';
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

const callDirectionIcon = dir =>
  dir === 'inbound' ? '↓' : dir === 'outbound' ? '↑' : '•';
const callStatusLabel = {
  ended: 'Atendida',
  missed: 'Perdida',
  rejected: 'Rejeitada',
  failed: 'Falhou',
  in_progress: 'Em progresso',
};
const appointmentStatusLabel = {
  scheduled: 'Agendado',
  attended: 'Compareceu',
  no_show: 'Não compareceu',
  cancelled: 'Cancelado',
};
const appointmentStatusColor = {
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  attended:
    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  no_show: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelled: 'bg-n-alpha-1 text-n-slate-12 bg-n-solid-2 ',
};

// KPIs resumo no topo da aba
const summary = computed(() => {
  if (!data.value) return null;
  const calls = data.value.call_records || [];
  const appts = data.value.appointments || [];
  const lastCall = calls[0];
  const totalClosed = calls
    .filter(c => c.fechamento_at)
    .reduce((s, c) => s + (c.fechamento_cents || 0), 0);
  return {
    total_calls: calls.length,
    answered_calls: calls.filter(c => c.final_status === 'ended').length,
    missed_calls: calls.filter(c => c.final_status === 'missed').length,
    appointments: appts.length,
    attended: appts.filter(a => a.status === 'attended').length,
    no_shows: appts.filter(a => a.status === 'no_show').length,
    total_closed_cents: totalClosed,
    last_call_at: lastCall?.created_at,
    last_disposition: lastCall?.disposition,
  };
});

const latestScore = computed(() => (data.value?.lead_scores || [])[0]);
</script>

<template>
  <div class="kanban-voip-tab space-y-4">
    <div v-if="loading" class="flex justify-center items-center py-8">
      <span
        class="w-5 h-5 border-2 border-t-woot-500 border-b-transparent rounded-full animate-spin"
      />
    </div>

    <div
      v-else-if="error"
      class="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-3 text-xs text-red-700 dark:text-red-300"
    >
      ❌ {{ error }}
    </div>

    <template v-else-if="data">
      <!-- Card de KPIs -->
      <div
        v-if="summary"
        class="bg-n-solid-2 rounded-xl border border-n-weak p-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Resumo VOIP</h3>
        <div class="grid grid-cols-3 gap-3 text-center">
          <div>
            <p class="text-lg font-extrabold text-n-slate-12">
              {{ summary.total_calls }}
            </p>
            <p class="text-[10px] text-n-slate-11">Chamadas</p>
          </div>
          <div>
            <p
              class="text-lg font-extrabold text-green-600 dark:text-green-400"
            >
              {{ summary.answered_calls }}
            </p>
            <p class="text-[10px] text-n-slate-11">Atendidas</p>
          </div>
          <div>
            <p class="text-lg font-extrabold text-rose-600 dark:text-rose-400">
              {{ summary.missed_calls }}
            </p>
            <p class="text-[10px] text-n-slate-11">Perdidas</p>
          </div>
          <div>
            <p class="text-lg font-extrabold text-n-slate-12">
              {{ summary.appointments }}
            </p>
            <p class="text-[10px] text-n-slate-11">Agendamentos</p>
          </div>
          <div>
            <p
              class="text-lg font-extrabold text-green-600 dark:text-green-400"
            >
              {{ summary.attended }}
            </p>
            <p class="text-[10px] text-n-slate-11">Compareceu</p>
          </div>
          <div>
            <p class="text-lg font-extrabold text-rose-600 dark:text-rose-400">
              {{ summary.no_shows }}
            </p>
            <p class="text-[10px] text-n-slate-11">Não compareceu</p>
          </div>
        </div>
        <div
          v-if="summary.total_closed_cents > 0"
          class="mt-3 pt-3 border-t border-n-weak text-center"
        >
          <p class="text-xl font-extrabold text-green-600 dark:text-green-400">
            {{ formatMoney(summary.total_closed_cents) }}
          </p>
          <p class="text-[10px] text-n-slate-11">Total fechado</p>
        </div>
      </div>

      <!-- Score atual -->
      <div
        v-if="latestScore"
        class="bg-n-solid-2 rounded-xl border border-n-weak p-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Score atual</h3>
        <div class="flex items-center gap-3">
          <div
            class="text-3xl font-extrabold"
            :class="
              latestScore.score >= 70
                ? 'text-red-500'
                : latestScore.score >= 40
                  ? 'text-amber-500'
                  : 'text-sky-500'
            "
          >
            ⚡ {{ latestScore.score }}
          </div>
          <div class="text-[11px] text-n-slate-11">
            <p>{{ latestScore.lead_profile || '—' }}</p>
            <p>Atualizado {{ relativeTime(latestScore.scored_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Chamadas recentes -->
      <div
        v-if="data.call_records && data.call_records.length"
        class="bg-n-solid-2 rounded-xl border border-n-weak p-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
          Últimas chamadas
        </h3>
        <div class="space-y-2">
          <div
            v-for="call in data.call_records.slice(0, 8)"
            :key="call.id"
            class="flex items-start gap-2 text-xs border-b border-n-weak pb-2 last:border-0 last:pb-0"
          >
            <span class="text-base shrink-0">{{
              callDirectionIcon(call.direction)
            }}</span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-medium text-n-slate-12">
                  {{ callStatusLabel[call.final_status] || call.final_status }}
                </span>
                <span v-if="call.duration_seconds" class="text-n-slate-11">{{
                  formatDuration(call.duration_seconds)
                }}</span>
                <span
                  v-if="call.disposition"
                  class="text-[10px] px-1.5 py-0.5 rounded-full bg-woot-100 text-woot-700 dark:bg-woot-900/40 dark:text-woot-300"
                >
                  {{ call.disposition }}
                </span>
                <span
                  v-if="call.fechamento_cents"
                  class="text-[10px] font-semibold text-green-600 dark:text-green-400"
                >
                  💰 {{ formatMoney(call.fechamento_cents) }}
                </span>
                <span
                  v-if="call.perdido_at"
                  class="text-[10px] text-rose-600 dark:text-rose-400"
                >
                  ❌ {{ call.perdido_reason || 'perdido' }}
                </span>
              </div>
              <p class="text-[10px] text-n-slate-11 mt-0.5">
                {{ formatDate(call.created_at) }} ·
                {{ relativeTime(call.created_at) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Agendamentos -->
      <div
        v-if="data.appointments && data.appointments.length"
        class="bg-n-solid-2 rounded-xl border border-n-weak p-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Agendamentos</h3>
        <div class="space-y-2">
          <div
            v-for="appt in data.appointments.slice(0, 6)"
            :key="appt.id"
            class="flex items-center gap-2 text-xs border-b border-n-weak pb-2 last:border-0 last:pb-0"
          >
            <span class="text-base shrink-0">📅</span>
            <div class="flex-1 min-w-0">
              <p class="font-medium text-n-slate-12">
                {{ formatDate(appt.scheduled_at) }}
              </p>
              <p
                v-if="appt.description"
                class="text-[10px] text-n-slate-11 truncate"
              >
                {{ appt.description }}
              </p>
            </div>
            <span
              class="text-[10px] px-2 py-0.5 rounded-full shrink-0"
              :class="
                appointmentStatusColor[appt.status] ||
                'bg-n-alpha-1 text-n-slate-12'
              "
            >
              {{ appointmentStatusLabel[appt.status] || appt.status }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="!data.call_records?.length && !data.appointments?.length"
        class="bg-n-solid-2/50 rounded-xl p-6 text-center text-sm text-n-slate-11"
      >
        Sem histórico VOIP ainda pra esse contato.
      </div>
    </template>
  </div>
</template>
